import { motion, useReducedMotion, type Transition } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import studioDesk from '@/assets/studio/DSC_8466.webp'
import { useTranslation } from '@/i18n'

/*
 * Critically damped (no bounce): these settle as the page scrolls, nothing
 * was flicked, so an overshoot would read as wobble.
 */
const SETTLE: Transition = { type: 'spring', bounce: 0, duration: 0.6 }

/** Fade and rise into place; under reduced motion, only the fade. */
const riseProps = (reduce: boolean | null) => ({
  initial: { opacity: 0, y: reduce ? 0 : 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
})

/**
 * Who we are, in the manner of an Apple product page: one large statement,
 * a studio photo as the visual, and three short facts in place of the old
 * "Why Choose Us?" checklist, which this section absorbed.
 */
export function WhatIsEvtimia() {
  const { t } = useTranslation()
  const copy = t.whatIsEvtimia
  const rise = riseProps(useReducedMotion())

  return (
    <section id="about" className="py-20 sm:py-32">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        <motion.div {...rise} transition={SETTLE} className="max-w-4xl">
          <p className="text-sm sm:text-base font-semibold text-primary-text mb-4">{copy.eyebrow}</p>
          {/* Display type: tight leading and negative tracking, which large
              text needs to read as one shape rather than spaced-out letters. */}
          <h2 className="text-[clamp(2.25rem,6vw,4.5rem)] font-extrabold leading-[1.05] tracking-[-0.02em] text-foreground text-balance">
            {copy.statement}
          </h2>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-muted-foreground max-w-2xl">{copy.intro}</p>
        </motion.div>

        <motion.figure
          {...rise}
          transition={{ ...SETTLE, delay: 0.1 }}
          className="relative mt-14 sm:mt-20 overflow-hidden rounded-3xl shadow-2xl shadow-foreground/10"
        >
          {/* The studio, not the team: the team photo
              (src/assets/team/team-group.jpg) went out of date in October 2026,
              and a photo with no people in it can't. Swap a new team photo in
              here once there is one, with its alt and caption in whatIsEvtimia. */}
          <img
            src={studioDesk}
            alt={copy.photoAlt}
            width={1200}
            height={798}
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover"
          />
          <figcaption className="absolute inset-x-3 bottom-3 sm:inset-x-auto sm:left-5 sm:bottom-5 rounded-xl border border-white/40 bg-white/60 dark:border-white/10 dark:bg-black/45 backdrop-blur-xl backdrop-saturate-150 px-4 py-2.5 text-sm font-semibold tracking-[0.01em] text-foreground">
            {copy.photoCaption}
          </figcaption>
        </motion.figure>

        <dl className="mt-14 sm:mt-20 grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-8">
          {copy.facts.map((fact, i) => (
            <motion.div key={fact.title} {...rise} transition={{ ...SETTLE, delay: 0.08 * i }} className="border-t border-border pt-6">
              <dt className="text-lg sm:text-xl font-bold tracking-[-0.01em] text-foreground">{fact.title}</dt>
              <dd className="mt-2 text-base leading-relaxed text-muted-foreground">{fact.body}</dd>
            </motion.div>
          ))}
        </dl>

        <motion.div {...rise} transition={SETTLE} className="mt-12">
          <Link
            to="/about"
            className="group inline-flex items-center gap-1.5 text-lg font-semibold text-primary-text hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            {copy.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
